import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FiCamera, FiChevronLeft, FiChevronRight, FiClock, FiAlertTriangle, FiSend } from "react-icons/fi";
import { startQuiz, resumeQuiz, submitAnswer, autoCompleteQuiz, skipQuestion, reportViolation, sendHeartbeat } from "../api/client";
import ProgressBar from "../components/ProgressBar";
import ThemeSelector from "../components/ThemeSelector";
import Proctor from "../components/Proctor";

const MAJOR_TYPES = new Set(["tab_switch", "phone_detected", "no_face", "virtual_camera", "external_display"]);
const BASE_TIME_SECONDS = 45 * 60;   // 45 minutes normal time
// Grace disabled — hard 45-minute window. Re-enable by setting this back to
// 10 * 60 (and matching backend/app/routers/quiz.py GRACE_TIME_SECONDS). All
// grace-gated UI (warning banner, modal, red timer tint) naturally
// self-disables when the threshold is 0 because its triggers require
// `remaining <= GRACE_TIME_SECONDS && remaining > 0`.
const GRACE_TIME_SECONDS = 0;
const TOTAL_TIME_SECONDS = BASE_TIME_SECONDS + GRACE_TIME_SECONDS; // = 45 min
const CAMERA_CHECK_FRAMES = 5; // check 5 frames for blackness
const BLACK_THRESHOLD = 15; // average pixel value below this = black/covered

// Known virtual camera identifiers (label substrings)
const VIRTUAL_CAMERA_KEYWORDS = [
  "obs virtual", "obs-camera", "manycam", "xsplit", "snap camera",
  "snapcamera", "droidcam", "iriun", "epoccam", "virtual cam",
  "virtualcam", "mmhmm", "chromacam", "splitcam", "youcam",
  "camtwist", "fake", "newtek ndi", "ndi video", "logi capture",
  "streamlabs", "prism live", "screen capture",
];

export default function Quiz() {
  const [sessionId, setSessionId] = useState(() =>
    sessionStorage.getItem("aiq_session_id")
  );
  const [questions, setQuestions] = useState([]); // all fetched questions in order
  const [answers, setAnswers] = useState({}); // { questionId: { optionId, isCorrect, correctOptionId } }
  const [currentIndex, setCurrentIndexRaw] = useState(0);

  // Wrap setCurrentIndex to persist position to sessionStorage
  const setCurrentIndex = useCallback((valOrFn) => {
    setCurrentIndexRaw((prev) => {
      const next = typeof valOrFn === "function" ? valOrFn(prev) : valOrFn;
      try { sessionStorage.setItem("aiq_current_index", String(next)); } catch {}
      return next;
    });
  }, []);
  const [totalQuestions, setTotalQuestions] = useState(45);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [overallTimeLeft, setOverallTimeLeft] = useState(TOTAL_TIME_SECONDS);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [questionTimes, setQuestionTimes] = useState({}); // { questionId: accumulated seconds }
  const [violationWarning, setViolationWarning] = useState(null);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [quizComplete, setQuizComplete] = useState(false);
  const [graceActive, setGraceActive] = useState(false);     // last 10 min — warn but allow submit
  const [overtimeActive, setOvertimeActive] = useState(false); // full time expired but unanswered remain
  const [submitError, setSubmitError] = useState("");         // banner error when blocking submit
  const [showGraceAlert, setShowGraceAlert] = useState(false); // modal shown once when entering grace
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [cameraChecking, setCameraChecking] = useState(true);
  const [cameraCheckStep, setCameraCheckStep] = useState(""); // status message
  const [livenessStep, setLivenessStep] = useState(null); // null, "turn_left", "turn_right", "turn_left_again", "done"
  const [livenessStream, setLivenessStream] = useState(null);
  const cameraStreamRef = useRef(null);
  const cameraVideoRef = useRef(null);
  const navigate = useNavigate();
  const initCalled = useRef(false);
  const timerRef = useRef(null);
  const navigatingRef = useRef(false);
  // Refs mirror state so the timer's setInterval closure always reads current values.
  // Without this, the interval would see the first-render snapshot of answers/totalQuestions.
  const answersRef = useRef({});
  const totalQuestionsRef = useRef(45);
  const graceActiveRef = useRef(false);

  // Keep refs in sync so timer callbacks always read live values.
  useEffect(() => { answersRef.current = answers; }, [answers]);
  useEffect(() => { totalQuestionsRef.current = totalQuestions; }, [totalQuestions]);
  useEffect(() => { graceActiveRef.current = graceActive; }, [graceActive]);

  // Track time on current question
  const recordQuestionTime = useCallback(() => {
    if (questions[currentIndex]) {
      const qId = questions[currentIndex].id;
      const elapsed = (Date.now() - questionStartTime) / 1000;
      setQuestionTimes((prev) => ({
        ...prev,
        [qId]: (prev[qId] || 0) + elapsed,
      }));
    }
  }, [currentIndex, questions, questionStartTime]);

  const handleViolation = useCallback((type, desc) => {
    console.warn(`[Proctor] ${type}: ${desc}`);
    if (MAJOR_TYPES.has(type)) {
      setViolationWarning({ type, desc });
      setTimeout(() => setViolationWarning(null), 6000);
    }
  }, []);

  // Check if violation response says quiz was auto-completed
  const checkViolationAutoComplete = useCallback((violationResponse) => {
    if (violationResponse?.auto_completed) {
      setQuizComplete(true);
      clearInterval(timerRef.current);
      const sid = sessionStorage.getItem("aiq_session_id");
      sessionStorage.removeItem("aiq_session_id"); sessionStorage.removeItem("aiq_current_index");
      navigate(`/results/${sid}`);
    }
  }, [navigate]);

  // ── Camera pre-check: permission + virtual camera + blackness + liveness ──
  useEffect(() => {
    let cancelled = false;

    async function checkCamera() {
      setCameraChecking(true);
      setCameraError("");
      setCameraCheckStep("Requesting camera access...");

      // Step 1: Request camera permission
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: "user" },
          audio: false,
        });
      } catch (err) {
        if (cancelled) return;
        if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
          setCameraError("Camera permission was denied. Please allow camera access and refresh the page to start the assessment.");
        } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
          setCameraError("No camera detected. Please connect a webcam and refresh the page.");
        } else {
          setCameraError(`Camera error: ${err.message}. Please check your camera and refresh.`);
        }
        setCameraChecking(false);
        return;
      }

      if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
      cameraStreamRef.current = stream;

      // Step 2: Virtual camera detection via track label
      setCameraCheckStep("Verifying camera authenticity...");
      const videoTrack = stream.getVideoTracks()[0];
      const trackLabel = (videoTrack?.label || "").toLowerCase();
      const isVirtual = VIRTUAL_CAMERA_KEYWORDS.some(kw => trackLabel.includes(kw));

      if (isVirtual) {
        setCameraError(
          `Virtual camera detected: "${videoTrack.label}". Virtual cameras are not allowed during the assessment. Please disable it and use your physical webcam.`
        );
        setCameraChecking(false);
        stream.getTracks().forEach(t => t.stop());
        // Report violation if session exists
        const sid = sessionStorage.getItem("aiq_session_id");
        if (sid) {
          try {
            await reportViolation({
              session_id: parseInt(sid, 10),
              event_type: "virtual_camera",
              description: `Virtual camera detected: ${videoTrack.label}`,
            });
          } catch {}
        }
        return;
      }

      // Step 3: Create video element and wait for data
      const video = document.createElement("video");
      video.srcObject = stream;
      video.muted = true;
      video.playsInline = true;
      cameraVideoRef.current = video;

      try {
        await video.play();
        await new Promise((resolve) => {
          if (video.readyState >= 2) return resolve();
          video.addEventListener("loadeddata", resolve, { once: true });
        });
        await new Promise(r => setTimeout(r, 1500));
      } catch {
        if (cancelled) return;
        setCameraError("Could not start video stream. Please check your camera and refresh.");
        setCameraChecking(false);
        stream.getTracks().forEach(t => t.stop());
        return;
      }

      if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }

      // Step 4: Check frames for blackness (camera covered)
      setCameraCheckStep("Checking camera feed...");
      const canvas = document.createElement("canvas");
      canvas.width = 64;
      canvas.height = 48;
      const ctx = canvas.getContext("2d");
      let blackFrames = 0;

      for (let i = 0; i < CAMERA_CHECK_FRAMES; i++) {
        ctx.drawImage(video, 0, 0, 64, 48);
        const imageData = ctx.getImageData(0, 0, 64, 48);
        const pixels = imageData.data;
        let totalBrightness = 0;
        for (let p = 0; p < pixels.length; p += 4) {
          totalBrightness += (pixels[p] + pixels[p + 1] + pixels[p + 2]) / 3;
        }
        const avgBrightness = totalBrightness / (pixels.length / 4);
        if (avgBrightness < BLACK_THRESHOLD) blackFrames++;
        if (i < CAMERA_CHECK_FRAMES - 1) await new Promise(r => setTimeout(r, 300));
      }

      if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }

      if (blackFrames >= CAMERA_CHECK_FRAMES - 1) {
        setCameraError("Your camera appears to be covered or producing a black image. Please uncover your camera and refresh the page.");
        setCameraChecking(false);
        stream.getTracks().forEach(t => t.stop());
        return;
      }

      // Step 5: Liveness check — user must turn head left then right
      setCameraCheckStep("Loading face detection for liveness check...");

      // Load face-api for liveness (reuse the Proctor's loader)
      let faceReady = false;
      if (window.faceapi) {
        faceReady = true;
      } else {
        const SCRIPT_URLS = [
          "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js",
          "https://unpkg.com/face-api.js@0.22.2/dist/face-api.min.js",
        ];
        const MODEL_URLS = [
          "https://justadudewhohacks.github.io/face-api.js/models",
          "https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/weights",
        ];

        for (const url of SCRIPT_URLS) {
          try {
            await new Promise((res, rej) => {
              const s = document.createElement("script"); s.src = url;
              s.onload = res; s.onerror = rej;
              document.head.appendChild(s);
            });
            if (window.faceapi) break;
          } catch {}
        }

        if (window.faceapi) {
          for (const mUrl of MODEL_URLS) {
            try {
              const r = await fetch(`${mUrl}/tiny_face_detector_model-weights_manifest.json`, { method: "HEAD", mode: "cors" });
              if (r.ok) {
                await Promise.all([
                  window.faceapi.nets.tinyFaceDetector.loadFromUri(mUrl),
                  window.faceapi.nets.faceLandmark68TinyNet.loadFromUri(mUrl),
                ]);
                faceReady = true;
                break;
              }
            } catch {}
          }
        }
      }

      if (!faceReady || cancelled) {
        // If face detection can't load, skip liveness but allow quiz
        console.warn("[Camera] Face detection unavailable — skipping liveness check");
        stream.getTracks().forEach(t => t.stop());
        setCameraReady(true);
        setCameraChecking(false);
        return;
      }

      // Liveness: require user to turn head left, right, left
      setCameraChecking(false);
      setLivenessStream(stream);
      livenessProgressRef.current = 0; // reset progress
      setLivenessStep("turn_left");

      // The liveness UI will handle the rest (see livenessStep state)
      // The stream stays open for the liveness check video preview
    }

    checkCamera();
    return () => {
      cancelled = true;
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  // ── Liveness check logic: detect head turns (left → right → left) ──
  const livenessVideoRef = useRef(null);
  const livenessCanvasRef = useRef(null);
  // Use refs for step progress so useEffect restart doesn't lose state
  const livenessProgressRef = useRef(0); // 0=waiting left, 1=passed left, 2=passed right, 3=passed second left (done)

  useEffect(() => {
    if (!livenessStep || livenessStep === "done" || !livenessStream) return;
    let running = true;

    async function detectLoop() {
      const video = livenessVideoRef.current;
      if (!video || !window.faceapi) return;

      // Ensure video is playing
      if (video.srcObject !== livenessStream) {
        video.srcObject = livenessStream;
        video.muted = true;
        video.playsInline = true;
        try { await video.play(); } catch {}
        await new Promise(r => setTimeout(r, 500));
      }

      while (running && video.readyState >= 2) {
        try {
          const detections = await window.faceapi
            .detectAllFaces(video, new window.faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.3 }))
            .withFaceLandmarks(true);

          if (detections.length > 0) {
            const largest = detections.reduce((a, b) =>
              b.detection.box.width * b.detection.box.height > a.detection.box.width * a.detection.box.height ? b : a
            );
            const landmarks = largest.landmarks;
            if (landmarks) {
              const nose = landmarks.getNose();
              const jaw = landmarks.getJawOutline();
              if (nose.length > 0 && jaw.length > 0) {
                const noseTip = nose[3] || nose[Math.floor(nose.length / 2)];
                const jawL = jaw[0];
                const jawR = jaw[jaw.length - 1];
                const jawW = Math.abs(jawR.x - jawL.x);
                const jawCx = (jawL.x + jawR.x) / 2;

                if (jawW > 0) {
                  const offsetX = (noseTip.x - jawCx) / jawW;
                  const progress = livenessProgressRef.current;

                  // Step 1: Turn left (offset > 0.2 means head turned left)
                  if (progress === 0 && offsetX > 0.2) {
                    livenessProgressRef.current = 1;
                    setLivenessStep("turn_right");
                  }
                  // Step 2: Turn right (offset < -0.2 means head turned right)
                  if (progress === 1 && offsetX < -0.2) {
                    livenessProgressRef.current = 2;
                    setLivenessStep("turn_left_again");
                  }
                  // Step 3: Turn left again
                  if (progress === 2 && offsetX > 0.2) {
                    livenessProgressRef.current = 3;
                    setLivenessStep("done");
                    running = false;
                    livenessStream.getTracks().forEach(t => t.stop());
                    setLivenessStream(null);
                    setCameraReady(true);
                    return;
                  }
                }
              }
            }
          }
        } catch {}
        await new Promise(r => setTimeout(r, 200));
      }
    }

    detectLoop();
    return () => { running = false; };
  }, [livenessStep, livenessStream]);

  // Initialize quiz — only after camera check passes
  useEffect(() => {
    if (!cameraReady) return;
    if (initCalled.current) return;
    initCalled.current = true;

    // Apply a /resume response to component state. Used by both the
    // sessionStorage-based path and the post-/start recovery path so a candidate
    // who lost their tab (empty sessionStorage) still gets their prior answers
    // and the same question grid back, not just a lone question.
    const applyResumeData = (data) => {
      setSessionId(data.session_id);
      setTotalQuestions(data.total_questions);

      const restoredQuestions = [];
      const restoredAnswers = {};
      const restoredTimes = {};

      for (const item of data.questions_in_order) {
        restoredQuestions.push(item.question);
        if (item.is_answered) {
          restoredAnswers[item.question.id] = {
            optionId: item.selected_option_id,
            isCorrect: item.is_correct,
            correctOptionId: item.correct_option_id,
          };
          if (item.time_spent_seconds != null) {
            restoredTimes[item.question.id] = item.time_spent_seconds;
          }
        }
      }

      setQuestions(restoredQuestions);
      setAnswers(restoredAnswers);
      answersRef.current = restoredAnswers;
      setQuestionTimes(restoredTimes);
      totalQuestionsRef.current = data.total_questions;

      const savedIdx = parseInt(sessionStorage.getItem("aiq_current_index"), 10);
      const resumeIdx = (!isNaN(savedIdx) && savedIdx >= 0 && savedIdx < restoredQuestions.length)
        ? savedIdx
        : data.current_index;
      setCurrentIndex(resumeIdx);
      setQuestionStartTime(Date.now());

      setOverallTimeLeft(data.remaining_seconds);

      if (data.remaining_seconds > 0 && data.remaining_seconds <= GRACE_TIME_SECONDS) {
        graceActiveRef.current = true;
        setGraceActive(true);
      }

      if (data.remaining_seconds <= 0) {
        const answeredCount = Object.keys(restoredAnswers).length;
        if (answeredCount >= data.total_questions) {
          handleTimeUp();
          return true;
        }
        setOvertimeActive(true);
      }

      setLoading(false);
      return true;
    };

    const initQuiz = async () => {
      const existingSessionId = sessionStorage.getItem("aiq_session_id");

      if (existingSessionId) {
        try {
          const res = await resumeQuiz(parseInt(existingSessionId, 10));
          applyResumeData(res.data);
          return;
        } catch (err) {
          // 404: session truly doesn't exist — clear and start fresh.
          // 400: session exists but is flagged completed. Keep the ID so the
          // /start glitch-recovery below can un-complete it. Clearing here
          // orphans in-progress answers — the exact bug we're protecting against.
          // 5xx / network: keep ID so a later refresh can retry.
          if (err.response?.status === 404) {
            sessionStorage.removeItem("aiq_session_id"); sessionStorage.removeItem("aiq_current_index");
          } else if (err.response?.status !== 400) {
            console.warn("Resume failed (transient) — keeping session for retry:", err);
            setError("Connection error while loading your assessment. Please refresh the page to resume.");
            setLoading(false);
            return;
          }
        }
      }

      // No existing session or resume failed — start (or pick up an existing incomplete)
      try {
        const res = await startQuiz();
        const newSessionId = res.data.session_id;
        sessionStorage.setItem("aiq_session_id", newSessionId);
        // Rehydrate full state via /resume so previously answered questions,
        // skipped questions, and remaining time are all restored. /start alone
        // only returns one question — that's fine for a brand-new session, but
        // a candidate who left mid-quiz would otherwise see "Question 1" with
        // no prior progress. Falling back to the /start response if /resume
        // itself fails keeps this a strict upgrade.
        try {
          const resumeRes = await resumeQuiz(newSessionId);
          sessionStorage.setItem(
            "aiq_current_index",
            String(resumeRes.data.current_index ?? 0)
          );
          applyResumeData(resumeRes.data);
          return;
        } catch (resumeErr) {
          console.warn("Post-start resume failed, using /start payload:", resumeErr);
          setSessionId(newSessionId);
          sessionStorage.setItem("aiq_current_index", "0");
          setTotalQuestions(res.data.total_questions);
          setQuestions([res.data.question]);
          setQuestionStartTime(Date.now());
          setLoading(false);
        }
      } catch (err) {
        if (err.response?.status === 400) {
          sessionStorage.removeItem("aiq_session_id"); sessionStorage.removeItem("aiq_current_index");
          navigate("/welcome");
          return;
        }
        setError("Failed to start quiz. Please try again.");
        setLoading(false);
      }
    };

    initQuiz();
  }, [navigate, cameraReady]);

  // Overall countdown timer (45 min normal + 10 min grace).
  useEffect(() => {
    if (loading || quizComplete || overtimeActive) return;

    timerRef.current = setInterval(() => {
      setOverallTimeLeft((prev) => {
        // Enter grace period when crossing into the last 10 minutes — show alert once.
        if (!graceActiveRef.current && prev <= GRACE_TIME_SECONDS) {
          graceActiveRef.current = true;
          setGraceActive(true);
          setShowGraceAlert(true);
        }
        if (prev <= 1) {
          clearInterval(timerRef.current);
          handleTimeUp();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerRef.current);
  }, [loading, quizComplete, overtimeActive]);

  // Heartbeat: every 5s while the quiz page is active, tell the server the
  // candidate spent 5 more seconds here. Server-side active_seconds is the
  // source of truth for remaining time — refreshes and network gaps don't
  // eat into the candidate's clock because heartbeats only fire while the
  // page is loaded.
  useEffect(() => {
    if (loading || quizComplete) return;
    const sid = sessionId || sessionStorage.getItem("aiq_session_id");
    if (!sid) return;

    // Bumped from 5s to 10s to halve DB write load during busy assessment
    // windows (100 concurrent candidates → 200 req/min on this endpoint
    // alone, each UPDATE-ing quiz_sessions.active_seconds). 10s granularity
    // is still fine for the active-time timer — candidates don't see
    // sub-10s differences in remaining time.
    const HEARTBEAT_INTERVAL_MS = 10000;
    const DELTA_SECONDS = 10;
    const iv = setInterval(() => {
      sendHeartbeat(parseInt(sid, 10), DELTA_SECONDS).catch((err) => {
        // Transient heartbeat failure is tolerable — next tick will retry.
        // Don't surface an error to the candidate for this.
        console.warn("[heartbeat] failed", err?.response?.status || err?.message);
      });
    }, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(iv);
  }, [loading, quizComplete, sessionId]);

  // Record tab refresh as minor violation
  useEffect(() => {
    const handleBeforeUnload = () => {
      const sid = sessionStorage.getItem("aiq_session_id");
      const token = localStorage.getItem("aiq_token");
      if (sid && token) {
        const apiBase = process.env.REACT_APP_API_URL || "http://localhost:8000";
        fetch(`${apiBase}/api/proctoring/violation`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          keepalive: true,
          body: JSON.stringify({
            session_id: parseInt(sid, 10),
            event_type: "tab_refresh",
            description: "Page was refreshed or navigated away during quiz",
          }),
        });
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  const handleTimeUp = async () => {
    // Never auto-submit while the candidate still has unanswered questions — this
    // protects against glitches (network blips, page refreshes) that would otherwise
    // forfeit their attempt. Stop the timer, enter overtime mode, and let them finish.
    const answered = Object.keys(answersRef.current).length;
    const total = totalQuestionsRef.current;
    if (answered < total) {
      clearInterval(timerRef.current);
      setOvertimeActive(true);
      return;
    }

    setQuizComplete(true);
    try {
      const sid = sessionStorage.getItem("aiq_session_id");
      if (sid) {
        await autoCompleteQuiz(sid);
        sessionStorage.removeItem("aiq_session_id"); sessionStorage.removeItem("aiq_current_index");
        navigate(`/results/${sid}`);
      }
    } catch (err) {
      // Quiz may already be completed by violations
      const sid = sessionStorage.getItem("aiq_session_id");
      sessionStorage.removeItem("aiq_session_id"); sessionStorage.removeItem("aiq_current_index");
      if (sid) navigate(`/results/${sid}`);
    }
  };

  // Buffer for the next question from backend (avoids React batching issues)
  const pendingNextQuestion = useRef(null);

  const handleAnswer = async (optionId) => {
    if (submitting || quizComplete) return;
    const currentQuestion = questions[currentIndex];
    if (!currentQuestion) return;

    // If user clicks the option they already picked, no-op
    const existing = answers[currentQuestion.id];
    if (existing && existing.optionId === optionId) return;

    setSubmitting(true);

    const qId = currentQuestion.id;
    const elapsed = (Date.now() - questionStartTime) / 1000;
    const totalTimeOnQuestion = (questionTimes[qId] || 0) + elapsed;

    const payload = {
      question_id: qId,
      selected_option_id: optionId,
      time_spent_seconds: Math.round(totalTimeOnQuestion * 10) / 10,
    };

    // Submit with auto-retry. Transient errors (network, 5xx) retry up to 3x
    // with exponential backoff. 400 "already completed" triggers a one-time
    // resume-then-retry so glitch-completed sessions self-heal without the
    // candidate seeing an error. We never clear sessionStorage in this handler
    // — refresh-based recovery via /start must still be able to find the session.
    const MAX_RETRIES = 3;
    let lastErr = null;
    let triedResumeRecovery = false;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
      try {
        const res = await submitAnswer(sessionId, payload);

        setAnswers((prev) => ({
          ...prev,
          [qId]: {
            optionId,
            isCorrect: res.data.is_correct,
            correctOptionId: res.data.correct_option_id,
          },
        }));
        setQuestionTimes((prev) => ({ ...prev, [qId]: totalTimeOnQuestion }));

        if (res.data.is_complete) {
          setQuizComplete(true);
          clearInterval(timerRef.current);
          sessionStorage.removeItem("aiq_session_id");
          sessionStorage.removeItem("aiq_current_index");
          setTimeout(() => { navigate(`/results/${sessionId}`); }, 800);
        } else if (res.data.next_question && questions.length < totalQuestions) {
          pendingNextQuestion.current = res.data.next_question;
        }

        setSubmitError("");
        setSubmitting(false);
        return;
      } catch (err) {
        lastErr = err;
        const status = err.response?.status;

        // 400 = backend thinks session is completed. Try /resume once to
        // un-complete it (glitch recovery), then loop to retry the submit.
        if (status === 400 && !triedResumeRecovery) {
          triedResumeRecovery = true;
          try {
            await resumeQuiz(sessionId);
            continue; // retry submit on the revived session
          } catch (_) {
            setError("We couldn't save your last answer. Please refresh the page to continue — your progress is saved on the server.");
            setSubmitting(false);
            return;
          }
        }

        // Don't retry 4xx (client errors) — retrying won't help.
        if (status && status >= 400 && status < 500) break;

        // Network / 5xx: backoff and retry.
        if (attempt < MAX_RETRIES) {
          await new Promise((r) => setTimeout(r, 400 * Math.pow(2, attempt)));
          continue;
        }
      }
    }

    console.error("[submitAnswer] all retries failed", lastErr);
    setSubmitError("Couldn't save your answer — network issue. Click your option again to retry; your previous answers are safe.");
    setSubmitting(false);
  };

  const goToPrevious = () => {
    if (currentIndex > 0 && !navigatingRef.current) {
      navigatingRef.current = true;
      recordQuestionTime();
      setCurrentIndex(currentIndex - 1);
      setQuestionStartTime(Date.now());
      setTimeout(() => { navigatingRef.current = false; }, 200);
    }
  };

  const goToNext = async () => {
    if (navigatingRef.current || quizComplete) return;
    navigatingRef.current = true;
    recordQuestionTime();

    // Case 1: There's already a next question in our local list
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setQuestionStartTime(Date.now());
      setTimeout(() => { navigatingRef.current = false; }, 200);
      return;
    }

    // Case 2: We have a buffered next question from answering
    if (pendingNextQuestion.current && questions.length < totalQuestions) {
      const newQ = pendingNextQuestion.current;
      pendingNextQuestion.current = null;
      const alreadyExists = questions.some((q) => q.id === newQ.id);
      if (!alreadyExists) {
        const newQuestions = [...questions, newQ];
        setQuestions(newQuestions);
        setCurrentIndex(newQuestions.length - 1);
        setQuestionStartTime(Date.now());
        setTimeout(() => { navigatingRef.current = false; }, 200);
        return;
      }
    } else if (pendingNextQuestion.current) {
      pendingNextQuestion.current = null; // discard if over limit
    }

    // Case 3: Don't exceed total questions
    if (questions.length >= totalQuestions) {
      setTimeout(() => { navigatingRef.current = false; }, 200);
      return;
    }

    // Case 4: Fetch a new question from backend (skip)
    try {
      const shownIds = questions.map((q) => q.id);
      const res = await skipQuestion(sessionId, shownIds);
      if (res.data.question) {
        const newQ = res.data.question;
        const alreadyExists = questions.some((q) => q.id === newQ.id);
        if (!alreadyExists) {
          const newQuestions = [...questions, newQ];
          setQuestions(newQuestions);
          setCurrentIndex(newQuestions.length - 1);
          setQuestionStartTime(Date.now());
        }
      }
    } catch (err) {
      console.warn("No more questions to skip to");
    }

    setTimeout(() => { navigatingRef.current = false; }, 200);
  };

  const handleSubmitQuiz = async () => {
    // Guard: never submit with unanswered questions. Point the user at the first
    // unanswered question and surface a visible error instead.
    const answeredNow = Object.keys(answers).length;
    if (answeredNow < totalQuestions) {
      setShowSubmitDialog(false);
      const remaining = totalQuestions - answeredNow;
      setSubmitError(
        `You have ${remaining} unanswered question${remaining === 1 ? "" : "s"}. Please answer all questions before submitting.`
      );
      for (let i = 0; i < questions.length; i++) {
        if (!answers[questions[i]?.id]) {
          recordQuestionTime();
          setCurrentIndex(i);
          setQuestionStartTime(Date.now());
          break;
        }
      }
      return;
    }

    setShowSubmitDialog(false);
    setSubmitError("");
    setQuizComplete(true);
    clearInterval(timerRef.current);
    try {
      await autoCompleteQuiz(sessionId);
      sessionStorage.removeItem("aiq_session_id"); sessionStorage.removeItem("aiq_current_index");
      navigate(`/results/${sessionId}`);
    } catch (err) {
      // Quiz may already be completed
      sessionStorage.removeItem("aiq_session_id"); sessionStorage.removeItem("aiq_current_index");
      navigate(`/results/${sessionId}`);
    }
  };

  const currentQuestion = questions[currentIndex];
  const isAnswered = currentQuestion && !!answers[currentQuestion?.id];
  const answeredCount = Object.keys(answers).length;

  // Two-phase timer display:
  //   - Before grace: show BASE countdown (45:00 → 00:00) — the grace window isn't exposed.
  //   - Grace/overtime: show GRACE countdown (10:00 → 00:00).
  // overallTimeLeft is the single source of truth (0–3300s); display just derives from it.
  const displaySeconds = graceActive
    ? Math.max(0, overallTimeLeft)
    : Math.max(0, overallTimeLeft - GRACE_TIME_SECONDS);

  // Format time
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const timeIsLow = overallTimeLeft <= 300;
  const timeIsCritical = overallTimeLeft <= 60;

  // Liveness check screen
  if (livenessStep && livenessStep !== "done") {
    const livenessStepNum = livenessStep === "turn_left" ? 1 : livenessStep === "turn_right" ? 2 : 3;
    const livenessColor = livenessStep === "turn_left_again" ? "#3b82f6" : livenessStep === "turn_right" ? "#22c55e" : "#f59e0b";
    const livenessInstruction = livenessStep === "turn_left"
      ? "Please turn your head to the LEFT"
      : livenessStep === "turn_right"
        ? "Now turn your head to the RIGHT"
        : "Turn your head to the LEFT once more";

    return (
      <div className="quiz-page">
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
          textAlign: "center",
          padding: "40px 20px",
        }}>
          <h2 style={{ color: "var(--text-primary)", margin: "0 0 8px", fontSize: "22px" }}>
            Liveness Verification
          </h2>
          <p style={{ color: "var(--text-secondary)", fontSize: "14px", maxWidth: "440px", marginBottom: "20px" }}>
            To confirm you are a real person, please follow the instructions below.
          </p>

          {/* Live video preview */}
          <div style={{
            position: "relative",
            width: "320px",
            height: "240px",
            borderRadius: "16px",
            overflow: "hidden",
            border: `3px solid ${livenessColor}`,
            marginBottom: "20px",
            background: "#000",
          }}>
            <video
              ref={livenessVideoRef}
              style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }}
              autoPlay
              muted
              playsInline
            />
          </div>

          <motion.div
            key={livenessStep}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              padding: "16px 32px",
              borderRadius: "12px",
              background: `${livenessColor}26`,
              border: `1px solid ${livenessColor}4D`,
            }}
          >
            <p style={{
              fontSize: "18px",
              fontWeight: 700,
              color: livenessColor,
              margin: 0,
            }}>
              {livenessInstruction}
            </p>
          </motion.div>

          <p style={{ color: "var(--text-muted)", fontSize: "12px", marginTop: "16px" }}>
            Step {livenessStepNum} of 3
          </p>
        </div>
      </div>
    );
  }

  // Camera check gate
  if (cameraChecking || cameraError) {
    return (
      <div className="quiz-page">
        <div style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "80vh",
          textAlign: "center",
          padding: "40px 20px",
        }}>
          {cameraChecking ? (
            <>
              <motion.div
                animate={{ scale: [1, 1.15, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
              >
                <FiCamera size={56} color="var(--color-highlight)" />
              </motion.div>
              <h2 style={{ color: "var(--text-primary)", margin: "24px 0 8px", fontSize: "22px" }}>
                Checking Camera Access
              </h2>
              <p style={{ color: "var(--text-secondary)", fontSize: "14px", maxWidth: "400px" }}>
                {cameraCheckStep || "Please allow camera access when prompted."}
              </p>
            </>
          ) : (
            <>
              <div style={{
                width: "72px",
                height: "72px",
                borderRadius: "50%",
                background: "rgba(239, 68, 68, 0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                marginBottom: "20px",
              }}>
                <FiCamera size={36} color="#ef4444" />
              </div>
              <h2 style={{ color: "#ef4444", margin: "0 0 12px", fontSize: "22px" }}>
                Camera Required
              </h2>
              <p style={{
                color: "var(--text-secondary)",
                fontSize: "15px",
                maxWidth: "460px",
                lineHeight: "1.6",
                marginBottom: "24px",
              }}>
                {cameraError}
              </p>
              <button
                onClick={() => window.location.reload()}
                style={{
                  padding: "12px 32px",
                  background: "var(--color-highlight)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontSize: "15px",
                  fontWeight: 600,
                }}
              >
                Retry
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="quiz-page">
        <div className="loading-screen">
          <motion.div
            className="loading-brain"
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
          >
            <span role="img" aria-label="brain">&#129504;</span>
          </motion.div>
          <p>Preparing your assessment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="quiz-page">
        <div className="error-screen">
          <p>{error}</p>
          <button
            onClick={() => setError("")}
            style={{
              marginTop: "16px",
              padding: "10px 24px",
              background: "var(--color-highlight)",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="quiz-page">
      {sessionId && (
        <Proctor sessionId={sessionId} onViolation={handleViolation} />
      )}
      {/* ThemeSelector hidden during quiz to avoid overlap with timer */}

      {/* Camera preview — only visible during violation warning */}
      <AnimatePresence>
        {violationWarning && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            style={{
              position: "fixed",
              bottom: "20px",
              right: "20px",
              zIndex: 9997,
              width: "200px",
              height: "150px",
              borderRadius: "12px",
              overflow: "hidden",
              border: "2px solid #ef4444",
              boxShadow: "0 4px 20px rgba(239,68,68,0.4)",
            }}
          >
            <video
              ref={(el) => {
                if (el && !el.srcObject) {
                  // Find the proctor video element and mirror its stream
                  const proctorVideo = document.querySelector('video[style*="top: -9999"]') ||
                    document.querySelector('video[style*="opacity: 0"]');
                  if (proctorVideo && proctorVideo.srcObject) {
                    el.srcObject = proctorVideo.srcObject;
                    el.play().catch(() => {});
                  }
                }
              }}
              style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }}
              muted
              playsInline
            />
            <div style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              padding: "4px 8px",
              background: "rgba(239,68,68,0.85)",
              color: "#fff",
              fontSize: "10px",
              fontWeight: 600,
              textAlign: "center",
            }}>
              Camera Preview
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Violation Warning Overlay */}
      <AnimatePresence>
        {violationWarning && (
          <motion.div
            initial={{ opacity: 0, y: -60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -60 }}
            style={{
              position: "fixed",
              top: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9999,
              background: "linear-gradient(135deg, #dc2626, #991b1b)",
              color: "#fff",
              padding: "16px 32px",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              gap: "12px",
              boxShadow: "0 8px 32px rgba(220, 38, 38, 0.4)",
              maxWidth: "600px",
              fontSize: "15px",
              fontWeight: 600,
            }}
          >
            <FiAlertTriangle size={24} />
            <div>
              <div style={{ fontSize: "16px", marginBottom: "2px" }}>
                Warning: Violation Detected
              </div>
              <div style={{ fontSize: "13px", fontWeight: 400, opacity: 0.9 }}>
                {violationWarning.desc || "Repeated violations may lead to disqualification."}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grace-start alert modal — fired once when the 45 min base expires */}
      <AnimatePresence>
        {showGraceAlert && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.65)",
              backdropFilter: "blur(4px)",
            }}
            onClick={() => setShowGraceAlert(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "linear-gradient(145deg, #1e293b, #0f172a)",
                borderRadius: "16px",
                padding: "32px",
                maxWidth: "440px",
                width: "90%",
                border: "2px solid #f59e0b",
                textAlign: "center",
                boxShadow: "0 20px 60px rgba(245, 158, 11, 0.3)",
              }}
            >
              <FiClock size={44} color="#f59e0b" style={{ marginBottom: "14px" }} />
              <h3 style={{ margin: "0 0 8px", fontSize: "22px", color: "#f1f5f9", fontWeight: 700 }}>
                Grace Time Started
              </h3>
              <p style={{ margin: "0 0 6px", color: "#fbbf24", fontSize: "15px", fontWeight: 600 }}>
                Only 10 minutes remaining.
              </p>
              <p style={{ margin: "0 0 20px", color: "#cbd5e1", fontSize: "13px", lineHeight: 1.5 }}>
                The 45-minute assessment time is over. Please finish your remaining questions within the 10-minute grace period.
              </p>
              <button
                onClick={() => setShowGraceAlert(false)}
                style={{
                  padding: "10px 28px",
                  background: "linear-gradient(135deg, #f59e0b, #d97706)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 600,
                }}
              >
                Continue
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grace period banner — last 10 minutes */}
      <AnimatePresence>
        {graceActive && !overtimeActive && !quizComplete && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            style={{
              position: "fixed",
              top: "60px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9996,
              background: "linear-gradient(135deg, #f59e0b, #d97706)",
              color: "#fff",
              padding: "10px 22px",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              boxShadow: "0 4px 20px rgba(245, 158, 11, 0.4)",
              fontSize: "14px",
              fontWeight: 600,
            }}
          >
            <FiClock size={18} />
            Only 10 minutes remaining — please finish all questions.
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overtime banner — time expired but candidate still has unanswered questions */}
      <AnimatePresence>
        {overtimeActive && !quizComplete && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            style={{
              position: "fixed",
              top: "60px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9996,
              background: "linear-gradient(135deg, #dc2626, #991b1b)",
              color: "#fff",
              padding: "12px 24px",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              boxShadow: "0 4px 20px rgba(220, 38, 38, 0.4)",
              fontSize: "14px",
              fontWeight: 600,
              maxWidth: "640px",
              textAlign: "center",
            }}
          >
            <FiAlertTriangle size={18} />
            Time is up. Please answer all remaining questions to submit — your attempt will not be auto-submitted.
          </motion.div>
        )}
      </AnimatePresence>

      {/* Submit-blocked error banner — dismisses on click */}
      <AnimatePresence>
        {submitError && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            onClick={() => setSubmitError("")}
            style={{
              position: "fixed",
              top: "110px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 9997,
              background: "linear-gradient(135deg, #dc2626, #991b1b)",
              color: "#fff",
              padding: "12px 24px",
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              boxShadow: "0 4px 20px rgba(220, 38, 38, 0.4)",
              fontSize: "14px",
              fontWeight: 600,
              maxWidth: "640px",
              cursor: "pointer",
            }}
          >
            <FiAlertTriangle size={18} />
            {submitError}
            <span style={{ fontSize: "11px", opacity: 0.8, marginLeft: "8px" }}>(click to dismiss)</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Submit Confirmation Dialog */}
      <AnimatePresence>
        {showSubmitDialog && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 9998,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(0,0,0,0.6)",
              backdropFilter: "blur(4px)",
            }}
            onClick={() => setShowSubmitDialog(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: "linear-gradient(145deg, #1e293b, #0f172a)",
                borderRadius: "16px",
                padding: "32px",
                maxWidth: "440px",
                width: "90%",
                border: "1px solid rgba(255,255,255,0.12)",
                textAlign: "center",
                boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
              }}
            >
              <FiAlertTriangle size={40} color={answeredCount < totalQuestions ? "#ef4444" : "#f59e0b"} style={{ marginBottom: "16px" }} />
              <h3 style={{ margin: "0 0 8px", fontSize: "20px", color: "#f1f5f9", fontWeight: 700 }}>
                {answeredCount < totalQuestions ? "Cannot Submit Yet" : "Submit Assessment?"}
              </h3>
              <p style={{ margin: "0 0 8px", color: "#cbd5e1", fontSize: "14px" }}>
                You have answered <strong style={{ color: "#f1f5f9" }}>{answeredCount}</strong> out of <strong style={{ color: "#f1f5f9" }}>{totalQuestions}</strong> questions.
              </p>
              {answeredCount < totalQuestions && (
                <p style={{ margin: "0 0 20px", color: "#ef4444", fontSize: "13px", fontWeight: 600 }}>
                  You must answer all {totalQuestions} questions before submitting.
                </p>
              )}
              <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                {answeredCount < totalQuestions ? (
                  <button
                    onClick={() => {
                      setShowSubmitDialog(false);
                      for (let i = 0; i < questions.length; i++) {
                        if (!answers[questions[i]?.id]) {
                          recordQuestionTime();
                          setCurrentIndex(i);
                          setQuestionStartTime(Date.now());
                          break;
                        }
                      }
                    }}
                    style={{
                      padding: "10px 24px",
                      background: "var(--color-highlight)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "8px",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: 600,
                    }}
                  >
                    Go to Unanswered
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => setShowSubmitDialog(false)}
                      style={{
                        padding: "10px 24px",
                        background: "rgba(255,255,255,0.08)",
                        color: "#f1f5f9",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: 500,
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmitQuiz}
                      style={{
                        padding: "10px 24px",
                        background: "linear-gradient(135deg, #22c55e, #16a34a)",
                        color: "#fff",
                        border: "none",
                        borderRadius: "8px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: 600,
                      }}
                    >
                      Confirm Submit
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Fixed Header: REC dot + Timer + Question Info (always visible) ── */}
      <div style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: "var(--bg-primary)",
        padding: "8px 24px",
        borderBottom: "1px solid var(--border-subtle)",
        boxShadow: "0 2px 12px rgba(0,0,0,0.15)",
      }}>
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          maxWidth: "1140px",
          margin: "0 auto",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            {/* Red recording dot + timer */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span className="proctoring-dot" title="Proctoring active" />
              <FiClock size={16} color={graceActive ? "#ef4444" : timeIsLow ? "#f59e0b" : "var(--text-secondary)"} />
              <span style={{
                fontSize: "20px",
                fontWeight: 700,
                fontFamily: "'JetBrains Mono', monospace",
                color: graceActive ? "#ef4444" : timeIsLow ? "#f59e0b" : "var(--text-primary)",
              }}>
                {formatTime(displaySeconds)}
              </span>
              {graceActive && (
                <span style={{
                  fontSize: "10px",
                  fontWeight: 700,
                  padding: "2px 7px",
                  borderRadius: "10px",
                  background: "#ef4444",
                  color: "#fff",
                  letterSpacing: "0.5px",
                }}>
                  GRACE
                </span>
              )}
            </div>
            {currentQuestion && (
              <>
                <span style={{
                  fontSize: "14px",
                  fontWeight: 700,
                  color: "var(--color-highlight)",
                  background: "rgba(99,102,241,0.12)",
                  padding: "3px 10px",
                  borderRadius: "8px",
                }}>
                  Q{currentIndex + 1}/{totalQuestions}
                </span>
                {currentQuestion.category && (
                  <span className="category-badge" style={{ margin: 0 }}>{currentQuestion.category}</span>
                )}
              </>
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
              {answeredCount}/{totalQuestions} answered
            </span>
            <button
              onClick={() => setShowSubmitDialog(true)}
              disabled={quizComplete}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "8px 20px",
                background: quizComplete ? "#6b7280" : "linear-gradient(135deg, #22c55e, #16a34a)",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                cursor: quizComplete ? "not-allowed" : "pointer",
                fontSize: "13px",
                fontWeight: 600,
              }}
            >
              <FiSend size={14} />
              Submit
            </button>
          </div>
        </div>

        {/* Timer progress bar — 0–100% of whichever phase is displayed */}
        <div style={{
          width: "100%",
          height: "3px",
          background: "var(--border-subtle)",
          borderRadius: "2px",
          marginTop: "6px",
          overflow: "hidden",
        }}>
          <motion.div
            style={{
              height: "100%",
              background: graceActive ? "#ef4444" : timeIsLow ? "#f59e0b" : "var(--color-highlight)",
              borderRadius: "2px",
            }}
            animate={{
              width: `${(displaySeconds / (graceActive ? GRACE_TIME_SECONDS : BASE_TIME_SECONDS)) * 100}%`,
            }}
            transition={{ duration: 0.5, ease: "linear" }}
          />
        </div>
      </div>

      {/* ── Main Content: Question + Navigation Panel ── */}
      <div style={{ width: "100%", maxWidth: "1140px" }}>
      <div style={{ display: "flex", gap: "16px", marginTop: "70px", alignItems: "flex-start" }}>
        {/* Left: Question Card */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {currentQuestion && (
            <AnimatePresence mode="wait">
              <motion.div
                key={currentQuestion.id}
                className="question-card"
                initial={{ opacity: 0, x: 60, scale: 0.96 }}
                animate={{ opacity: 1, x: 0, scale: 1, transition: { duration: 0.45, ease: "easeOut" } }}
                exit={{ opacity: 0, x: -60, scale: 0.96, transition: { duration: 0.3 } }}
              >
                <div className="question-header">
                  <span className="question-number">Q{currentIndex + 1}</span>
                  {isAnswered && (
                    <span style={{
                      fontSize: "12px",
                      padding: "2px 10px",
                      borderRadius: "12px",
                      fontWeight: 600,
                      background: "rgba(34, 197, 94, 0.15)",
                      color: "#22c55e",
                    }}>
                      Answered
                    </span>
                  )}
                </div>

                <h2 className="question-text">{currentQuestion.question_text}</h2>

                <div className="options-grid">
                  {currentQuestion.options.map((opt, i) => {
                    const answered = answers[currentQuestion.id];
                    const isSelected = answered?.optionId === opt.id;
                    const isDisabled = submitting || quizComplete;

                    return (
                      <motion.button
                        key={opt.id}
                        className={`option-btn ${isSelected ? "selected" : ""}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{
                          opacity: 1, y: 0,
                          transition: { delay: 0.1 + i * 0.08, duration: 0.35, ease: "easeOut" },
                        }}
                        onClick={() => handleAnswer(opt.id)}
                        disabled={isDisabled}
                        whileHover={!isDisabled ? { scale: 1.02 } : {}}
                        whileTap={!isDisabled ? { scale: 0.98 } : {}}
                      >
                        <span className="option-letter">{String.fromCharCode(65 + i)}</span>
                        <span className="option-text">{opt.option_text}</span>
                      </motion.button>
                    );
                  })}
                </div>
                {isAnswered && (
                  <p style={{
                    marginTop: "10px",
                    fontSize: "12px",
                    color: "var(--text-secondary)",
                    fontStyle: "italic",
                  }}>
                    You can change your answer by clicking a different option.
                  </p>
                )}

                {/* Navigation Buttons */}
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginTop: "24px",
                  paddingTop: "16px",
                  borderTop: "1px solid var(--border-subtle)",
                }}>
                  <button
                    onClick={goToPrevious}
                    disabled={currentIndex === 0}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "10px 20px",
                      background: currentIndex === 0 ? "transparent" : "var(--bg-card)",
                      color: currentIndex === 0 ? "var(--text-muted)" : "var(--text-primary)",
                      border: `1px solid ${currentIndex === 0 ? "transparent" : "var(--border-subtle)"}`,
                      borderRadius: "8px",
                      cursor: currentIndex === 0 ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: 500,
                      opacity: currentIndex === 0 ? 0.4 : 1,
                    }}
                  >
                    <FiChevronLeft size={16} />
                    Previous
                  </button>

                  <span style={{ fontSize: "13px", color: "var(--text-secondary)" }}>
                    {currentIndex + 1} of {totalQuestions}
                  </span>

                  <button
                    onClick={goToNext}
                    disabled={quizComplete}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "10px 20px",
                      background: quizComplete ? "transparent" : "var(--color-highlight)",
                      color: quizComplete ? "var(--text-muted)" : "#fff",
                      border: "none",
                      borderRadius: "8px",
                      cursor: quizComplete ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: 500,
                      opacity: quizComplete ? 0.4 : 1,
                    }}
                  >
                    {isAnswered ? "Next" : "Skip"}
                    <FiChevronRight size={16} />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>
          )}
        </div>

        {/* Right: Question Navigation Panel */}
        <div style={{
          width: "220px",
          flexShrink: 0,
          background: "var(--bg-card)",
          borderRadius: "12px",
          border: "1px solid var(--border-subtle)",
          padding: "14px",
          position: "sticky",
          top: "100px",
          maxHeight: "calc(100vh - 120px)",
          overflowY: "auto",
        }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: "var(--text-primary)", marginBottom: "10px" }}>
            Questions
          </div>
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(6, 1fr)",
            gap: "4px",
          }}>
            {Array.from({ length: totalQuestions }, (_, i) => {
              const q = questions[i];
              const isActive = i === currentIndex;
              const isAns = q && !!answers[q?.id];
              const isShown = i < questions.length;
              const isSkipped = isShown && !isAns;

              let bg = "rgba(255,255,255,0.04)"; // not yet shown
              let color = "var(--text-muted)";
              let border = "1px solid transparent";
              if (isActive) {
                bg = "var(--color-highlight)";
                color = "#fff";
                border = "1px solid var(--color-highlight)";
              } else if (isAns) {
                bg = "rgba(34, 197, 94, 0.2)";
                color = "#22c55e";
                border = "1px solid rgba(34, 197, 94, 0.3)";
              } else if (isSkipped) {
                bg = "rgba(239, 68, 68, 0.15)";
                color = "#ef4444";
                border = "1px solid rgba(239, 68, 68, 0.25)";
              }

              return (
                <button
                  key={i}
                  onClick={() => {
                    if (isShown && i !== currentIndex) {
                      recordQuestionTime();
                      setCurrentIndex(i);
                      setQuestionStartTime(Date.now());
                    }
                  }}
                  disabled={!isShown}
                  style={{
                    width: "100%",
                    aspectRatio: "1",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "6px",
                    background: bg,
                    color,
                    border,
                    fontSize: "11px",
                    fontWeight: isActive ? 700 : 500,
                    cursor: isShown ? "pointer" : "default",
                    opacity: isShown ? 1 : 0.3,
                    transition: "all 0.15s",
                    padding: 0,
                  }}
                  title={isAns ? `Q${i + 1} - Answered` : isSkipped ? `Q${i + 1} - Skipped` : `Q${i + 1}`}
                >
                  {i + 1}
                </button>
              );
            })}
          </div>
          {/* Legend */}
          <div style={{ marginTop: "12px", display: "flex", flexWrap: "wrap", gap: "8px", fontSize: "10px", color: "var(--text-muted)" }}>
            <span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: "var(--color-highlight)", display: "inline-block" }} /> Current
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: "rgba(34,197,94,0.5)", display: "inline-block" }} /> Answered
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
              <span style={{ width: 8, height: 8, borderRadius: 2, background: "rgba(239,68,68,0.5)", display: "inline-block" }} /> Skipped
            </span>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
